
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save, History } from '@mui/icons-material';
import { attendanceApi } from '../../../api/attendanceApi';
import { employeeApi } from '../../../api/employeeApi';
import { toast } from 'react-toastify';

interface OTEntry {
  id: string;
  employeeName: string;
  employeeCode: string;
  date: string;
  otHours: number;
  hotHours: number;
  remarks: string;
  createdBy: string;
  createdAt: string;
}

interface EmployeeOption {
  id: string;
  fullName: string;
  employeeCode: string;
  allowOvertime: boolean;
}

const getEmployeeLabel = (employee: EmployeeOption): string =>
  `${employee.fullName} (${employee.employeeCode})`;

const formatDate = (value: string): string => {
  if (!value) return '-';
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return value;
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = dt.getFullYear();
  return `${d}-${m}-${y}`;
};

const formatDateTime = (value: string): string => {
  if (!value) return '-';
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return value;
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${d}-${m}-${y} ${hh}:${mm}`;
};

const ManualOTPage: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employee, setEmployee] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [date, setDate] = useState('');
  // const [otHours, setOtHours] = useState('');
  // const [hotHours, setHotHours] = useState('');
  const [remarks, setRemarks] = useState('');
  const [otHistory, setOtHistory] = useState<OTEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otHours, setOtHours] = useState<number | "">("");
  const [hotHours, setHotHours] = useState<number | "">("");
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceApi.getManualOTHistory();
      setOtHistory(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch OT history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    let isCancelled = false;

    const timeoutId = window.setTimeout(async () => {
      setEmployeeLoading(true);
      try {
        const emps = await employeeApi.getAll(1, 50, employeeSearchText.trim() || undefined);
        if (!isCancelled) {
          setEmployees(
            emps
              .filter((e: any) => e.allowOvertime !== false)
              .map((e: any) => ({
                id: e.id,
                fullName: e.fullName || [e.firstName, e.lastName].filter(Boolean).join(' ') || e.name || '',
                employeeCode: e.employeeCode || '',
                allowOvertime: e.allowOvertime !== false,
              }))
          );
        }
      } catch {
        if (!isCancelled) {
          setEmployees([]);
        }
      } finally {
        if (!isCancelled) {
          setEmployeeLoading(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [employeeSearchText]);

  const employeeOptions = useMemo(() => {
    if (!selectedEmployee) {
      return employees;
    }

    const alreadyIncluded = employees.some((item) => item.id === selectedEmployee.id);
    return alreadyIncluded ? employees : [selectedEmployee, ...employees];
  }, [employees, selectedEmployee]);

  const validateForm = (): string | null => {
    if (!employee) return 'Please select an employee';
    if (!date) return 'Please select a date';
    if (!otHours && !hotHours) return 'Please enter OT or HOT hours';
    if (Number(otHours) < 0 || Number(hotHours) < 0) return 'Hours cannot be negative';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await attendanceApi.submitManualOT({
        employeeId: employee,
        date,
        otHours: Number(otHours) || 0,
        hotHours: Number(hotHours) || 0,
        remarks: remarks || undefined,
      });
      toast.success('OT/HOT entry saved successfully!');
      setEmployee('');
      setSelectedEmployee(null);
      setEmployeeSearchText('');
      setDate('');
      setOtHours('');
      setHotHours('');
      setRemarks('');
      fetchHistory();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save OT entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Manual OT/HOT for Employee
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        HR manually records overtime (OT) and holiday overtime (HOT) for employees.        
      </Typography>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Add OT/HOT Entry
              </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Autocomplete
                  size="small"
                  options={employeeOptions}
                  value={selectedEmployee}
                  inputValue={employeeSearchText}
                  loading={employeeLoading}
                  onChange={(_, newValue) => {
                    setSelectedEmployee(newValue);
                    setEmployee(newValue?.id ?? '');
                    setEmployeeSearchText(newValue ? getEmployeeLabel(newValue) : '');
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      setSelectedEmployee(null);
                      setEmployee('');
                      setEmployeeSearchText(newInputValue);
                    }

                    if (reason === 'clear') {
                      setSelectedEmployee(null);
                      setEmployee('');
                      setEmployeeSearchText('');
                    }
                  }}
                  getOptionLabel={getEmployeeLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee *"
                      placeholder="Search employee by name or code"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {employeeLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  label="Date *"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  inputProps={{
                    min: today, // ✅ disables past dates
                  }}
                />
                <TextField
                  label="OT Hours"
                  type="number"
                  size="small"
                  fullWidth
                  value={otHours}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Allow empty input
                    if (value === "") {
                      setOtHours("");
                      return;
                    }

                    let num = Number(value);

                    // Block negative values
                    if (num < 0) return;

                    setOtHours(num);
                  }}
                  onKeyDown={(e) => {
                    // Prevent typing negative sign
                    if (e.key === '-' || e.key === 'Minus') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText="Regular overtime hours"
                />

                <TextField
                  label="HOT Hours"
                  type="number"
                  size="small"
                  fullWidth
                  value={hotHours}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === "") {
                      setHotHours("");
                      return;
                    }

                    let num = Number(value);

                    if (num < 0) return;

                    setHotHours(num);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'Minus') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText="Holiday overtime hours"
                />
                <TextField
                  label="Remarks"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  fullWidth
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* History Section */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <History color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Recent OT/HOT Entries
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Employee</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="center"><strong>OT</strong></TableCell>
                      <TableCell align="center"><strong>HOT</strong></TableCell>
                      <TableCell><strong>Remarks</strong></TableCell>
                      <TableCell><strong>Created By</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No OT/HOT entries found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      otHistory.map((entry) => (
                        <TableRow key={entry.id} hover>
                          <TableCell>
                            {(entry.employeeName || '').trim() || '-'}
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {entry.employeeCode}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(entry.date)}</TableCell>
                          <TableCell align="center">{entry.otHours}h</TableCell>
                          <TableCell align="center">{entry.hotHours}h</TableCell>
                          <TableCell>{entry.remarks}</TableCell>
                          <TableCell>
                            {entry.createdBy || '-'}
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(entry.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManualOTPage;
